import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import {
  OtherInsuranceFormShape,
  defaultOtherEntry,
} from '../../hooks/useOtherInsuranceForm';
import { doPeriodsOverlap } from '../../utils';
import { useInsuranceStore } from '@store';

type SubmitEntryFn = (index: number) => Promise<boolean>;

const OVERLAP_ERROR =
  'Overlapping validity period with another insurance of the same type.';

export const useOtherInsuranceList = (
  fieldKey: 'primary_other' | 'secondary',
  submitEntryFn?: SubmitEntryFn,
) => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<OtherInsuranceFormShape>();

  const { fields, prepend, remove } = useFieldArray({
    control,
    name: 'entries',
  });

  const [expandedId, setExpandedId] = useState<string | null>(
    fields.length > 0 ? fields[0].id : null,
  );

  const prevLengthRef = useRef(fields.length);
  useEffect(() => {
    if (fields.length > prevLengthRef.current) {
      setExpandedId(fields[0].id);
    }
    prevLengthRef.current = fields.length;
  }, [fields]);

  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );

  const [submittingIndex, setSubmittingIndex] = useState<number | null>(null);

  const handleSubmitEntry = async (index: number) => {
    if (!submitEntryFn) return;
    setSubmittingIndex(index);
    try {
      await submitEntryFn(index);
    } finally {
      setSubmittingIndex(null);
    }
  };

  const expandedIndex = fields.findIndex(f => f.id === expandedId);
  const safeExpandedIndex = expandedIndex === -1 ? 0 : expandedIndex;

  const allEntries = useWatch({ control, name: 'entries' });

  const expandedEntry = allEntries[safeExpandedIndex];

  const expandedHasOverlap =
    expandedIndex !== -1 &&
    allEntries.length >= 2 &&
    !!expandedEntry?.start_date &&
    allEntries.some((other, j) => {
      if (j === expandedIndex) return false;
      if (!other.start_date) return false;
      return doPeriodsOverlap(
        expandedEntry.start_date,
        expandedEntry.end_date || undefined,
        other.start_date,
        other.end_date || undefined,
      );
    });

  const { setPrimaryOtherHasOverlap, setSecondaryHasOverlap } =
    useInsuranceStore();

  useEffect(() => {
    if (fieldKey === 'primary_other') {
      setPrimaryOtherHasOverlap(expandedHasOverlap);
    } else {
      setSecondaryHasOverlap(expandedHasOverlap);
    }
  }, [expandedHasOverlap]);

  const getLabel = (index: number) => {
    const paymentType = watch(`entries.${index}.payment_type`);
    const name = watch(`entries.${index}.insurance_company_name`);

    if (!name || paymentType?.toLowerCase().includes('cash'))
      return `Insurance ${index + 1}`;
    return name;
  };

  const handleAdd = () => {
    prepend(defaultOtherEntry);
  };

  const handleRemove = (index: number) => {
    remove(index);
    const nextField = fields.find((_, i) => i !== index);
    setExpandedId(nextField?.id ?? null);
    setPendingRemoveIndex(null);
  };

  const handleRequestRemove = (index: number) => setPendingRemoveIndex(index);
  const handleCancelRemove = () => setPendingRemoveIndex(null);

  const handleToggle = (id: string) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  const getEntryId = (index: number): string | undefined =>
    watch(`entries.${index}.id`) as string | undefined;

  const hasError = (index: number) =>
    !!errors.entries?.[index] ||
    (index === expandedIndex && expandedHasOverlap);

  const getOverlapError = (index: number): string | undefined =>
    index === expandedIndex && expandedHasOverlap ? OVERLAP_ERROR : undefined;

  const isAtLimit = fields.length >= 5;
  const isOnlyEntry = fields.length <= 1;

  return {
    fields,
    expandedId,
    getLabel,
    handleAdd,
    handleRemove,
    handleRequestRemove,
    handleCancelRemove,
    handleToggle,
    hasError,
    getOverlapError,
    pendingRemoveIndex,
    getEntryId,
    isAtLimit,
    isOnlyEntry,
    handleSubmitEntry,
    submittingIndex,
  };
};
